/**
 * Utilities for persisting auth state to localStorage.
 * Separated from authAtoms to avoid circular dependencies.
 */

import {
  ALL_PAGE_SCOPES,
  PageScope,
  OrgRole,
  UserRole,
  OrgFeature,
  MfaClaim,
} from "./types";

const DEFAULT_ROLE: UserRole = "TRIAL";

const ALL_ORG_FEATURES: OrgFeature[] = [
  "RECEIVABLES_PI_3RD_WIDGET",
  "RECEIVABLES_MONTHLY_AGING_EXPORT",
  "RECEIVABLES_PERCENT_CHART_VIEW",
  "RECEIVABLES_GROUP_AGING_BREAKDOWN",
];

const normalizeOrgFeature = (value: string): OrgFeature | null => {
  const trimmed = value.trim();
  return (ALL_ORG_FEATURES as readonly string[]).includes(trimmed)
    ? (trimmed as OrgFeature)
    : null;
};

const ALL_SCOPES: PageScope[] = ALL_PAGE_SCOPES;

const DEFAULT_SCOPES: PageScope[] = [
  "CASHFLOW",
  "DASHBOARDS",
  "TRANSACTIONS",
  "FILES_INTEGRATIONS",
  "FORECAST",
];

const normalizeScope = (s: string): PageScope | null => {
  const u = s.trim().toUpperCase();
  return (ALL_SCOPES as readonly string[]).includes(u)
    ? (u as PageScope)
    : null;
};

const normalizeRole = (role: string): UserRole => {
  const upper = role.trim().toUpperCase();
  if (upper === "ADMIN" || upper === "TRIAL" || upper === "USER") {
    return upper as UserRole;
  }
  return DEFAULT_ROLE;
};

export const safeParseJson = <T>(value: string | null): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch (error) {
    console.warn("Failed to parse stored JSON value", error);
    return null;
  }
};

/**
 * Returns JWT exp claim in seconds since epoch, or null if token is invalid/missing exp.
 */
export const getTokenExpiry = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1])) as { exp?: number };
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
};

/**
 * Safely decodes JWT payload; returns null when token is malformed.
 */
export const getTokenClaims = (
  token: string,
): Record<string, unknown> | null => {
  try {
    return JSON.parse(atob(token.split(".")[1])) as Record<string, unknown>;
  } catch {
    return null;
  }
};

/** True if token is expired or expires within bufferSeconds. */
export const isTokenExpiredOrNearExpiry = (
  token: string,
  bufferSeconds: number = 5 * 60,
): boolean => {
  const exp = getTokenExpiry(token);
  if (exp === null) return true;
  return exp <= Date.now() / 1000 + bufferSeconds;
};

export const tokenFromStorage =
  typeof window !== "undefined" ? localStorage.getItem("token") : null;

export const uidFromStorage =
  typeof window !== "undefined" ? localStorage.getItem("uid") : null;

export const rolesFromStorage =
  typeof window !== "undefined"
    ? safeParseJson<UserRole[]>(localStorage.getItem("roles"))
    : null;

export const scopesFromStorage =
  typeof window !== "undefined"
    ? safeParseJson<PageScope[]>(localStorage.getItem("scopes"))
    : null;

export const featuresFromStorage =
  typeof window !== "undefined"
    ? safeParseJson<OrgFeature[]>(localStorage.getItem("features"))
    : null;

export const emailFromStorage =
  typeof window !== "undefined" ? localStorage.getItem("email") : null;

export const orgRoleFromStorage =
  typeof window !== "undefined"
    ? (localStorage.getItem("org_role") as OrgRole | null)
    : null;

export const orgIdFromStorage =
  typeof window !== "undefined"
    ? (() => {
        const raw = localStorage.getItem("org_id");
        if (raw) {
          const parsed = Number(raw);
          if (Number.isFinite(parsed)) return parsed;
        }
        const token = localStorage.getItem("token");
        if (!token) return null;
        try {
          const payload = JSON.parse(atob(token.split(".")[1])) as Record<
            string,
            unknown
          >;
          const value = payload.org_id;
          if (typeof value === "number" && Number.isFinite(value)) return value;
          if (typeof value === "string" && value.trim().length > 0) {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : null;
          }
        } catch {
          return null;
        }
        return null;
      })()
    : null;

export const extractOrgRole = (
  claims: Record<string, unknown>,
): OrgRole | null => {
  const value = claims.org_role;
  if (value === "MANAGER" || value === "COLLECTOR" || value === "VIEWER") {
    return value;
  }
  return null;
};

export const extractOrgId = (
  claims: Record<string, unknown>,
): number | null => {
  const value = claims.org_id;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export const extractRoles = (claims: Record<string, unknown>): UserRole[] => {
  const value = claims.roles ?? claims.role;
  if (Array.isArray(value)) {
    const parsed = value
      .filter((role): role is string => typeof role === "string")
      .map(normalizeRole);
    return parsed.length ? parsed : [DEFAULT_ROLE];
  }

  if (typeof value === "string" && value.trim().length > 0) {
    return [normalizeRole(value)];
  }

  return [DEFAULT_ROLE];
};

export const extractScopes = (claims: Record<string, unknown>): PageScope[] => {
  const value = claims.scopes;
  if (Array.isArray(value)) {
    const parsed = value
      .filter((s): s is string => typeof s === "string")
      .map((scope) =>
        scope === "CUSTOMERS" || scope === "INVOICES" ? "RECEIVABLES" : scope,
      )
      .map(normalizeScope)
      .filter((s): s is PageScope => s !== null);
    const deduped = [...new Set(parsed)];
    if (parsed.length === 1 && parsed[0] === "CASHFLOW") {
      // Legacy token compatibility: backend treats CASHFLOW-only as full core defaults.
      return [...DEFAULT_SCOPES];
    }
    return deduped.length ? deduped : [...DEFAULT_SCOPES];
  }
  return [...DEFAULT_SCOPES];
};

export const extractFeatures = (
  claims: Record<string, unknown>,
): OrgFeature[] => {
  const value = claims.features;
  if (!Array.isArray(value)) {
    return [];
  }
  const parsed = value
    .filter((item): item is string => typeof item === "string")
    .map(normalizeOrgFeature)
    .filter((item): item is OrgFeature => item !== null);
  return [...new Set(parsed)];
};

export const extractMfaClaim = (
  claims: Record<string, unknown>,
): MfaClaim | null => {
  const value = claims.mfa;
  if (value === "not_required" || value === "pending" || value === "verified") {
    return value;
  }
  return null;
};

export const resolveMfaClaimFromStatus = (status: {
  required: boolean;
  phase: string;
}): MfaClaim => {
  if (!status.required) {
    return "not_required";
  }
  return status.phase === "verified" ? "verified" : "pending";
};

export const persistToken = (token: string | null) => {
  if (typeof window === "undefined") return;

  if (token) {
    localStorage.setItem("token", token);
  } else {
    localStorage.removeItem("token");
  }
};

export const persistUid = (uid: string | null) => {
  if (typeof window === "undefined") return;

  if (uid) {
    localStorage.setItem("uid", uid);
  } else {
    localStorage.removeItem("uid");
  }
};

export const persistRoles = (roles: UserRole[] | null) => {
  if (typeof window === "undefined") return;

  if (roles && roles.length > 0) {
    localStorage.setItem("roles", JSON.stringify(roles));
  } else {
    localStorage.removeItem("roles");
  }
};

export const persistScopes = (scopes: PageScope[] | null) => {
  if (typeof window === "undefined") return;

  if (scopes && scopes.length > 0) {
    localStorage.setItem("scopes", JSON.stringify(scopes));
  } else {
    localStorage.removeItem("scopes");
  }
};

export const persistEmail = (email: string | null) => {
  if (typeof window === "undefined") return;

  if (email) {
    localStorage.setItem("email", email);
  } else {
    localStorage.removeItem("email");
  }
};

export const persistOrgRole = (orgRole: OrgRole | null) => {
  if (typeof window === "undefined") return;
  if (orgRole) {
    localStorage.setItem("org_role", orgRole);
  } else {
    localStorage.removeItem("org_role");
  }
};

export const persistOrgId = (orgId: number | null) => {
  if (typeof window === "undefined") return;
  if (orgId != null) {
    localStorage.setItem("org_id", String(orgId));
  } else {
    localStorage.removeItem("org_id");
  }
};

export const persistFeatures = (features: OrgFeature[] | null) => {
  if (typeof window === "undefined") return;
  if (features && features.length > 0) {
    localStorage.setItem("features", JSON.stringify(features));
  } else {
    localStorage.removeItem("features");
  }
};
